import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import {
  Bookmark as BookmarkIcon,
  BookmarkPlus,
  CheckCircle2,
  Filter,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  Paperclip,
  PenSquare,
  Plus,
  RefreshCw,
  Rocket,
  Send,
  Sparkles,
  Tag,
  Trash2,
  Users,
  Globe,
  UserCircle,
  Building2,
  X,
  Search,
  Hash,
  Pin,
  Megaphone,
  Calendar as CalendarIcon,
  BarChart3,
  HelpCircle,
  Trophy,
  Award,
  Flag,
  Flame,
  TrendingUp,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@easyflow/ui-frontend/components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@easyflow/ui-frontend/components/ui/sheet';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { aurora, auroraPalette } from '@/components/aurora/tokens';
import { apiClient } from '@/lib/api';
import { usePullToRefresh } from '@/components/mobile/crm/usePullToRefresh';


import MobileTopBar, { type MobileTopBarAction } from '@/components/mobile/messenger/MobileTopBar';
import MobileComposerSheet from '@/components/mobile/messenger/MobileComposerSheet';

import type {
  FeedPost,
  FeedComment,
  FeedReaction,
  FeedAttachment,
  FeedTopic,
  ContentJson,
} from '@/types/feed';
import type {
  FeedReactionKind,
  FeedRsvpStatus,
  FeedTopicListItem,
  FeedTopicSuggestion,
  FeedEditorListItem,
  FeedPopularPost,
  FeedTrendingHashtag,
  FeedRsvpSummary,
  DirectoryUserLookup,
  WorkgroupLookup,
  DepartmentLookup,
} from '@easyflow/shared';

// Local narrow extensions: il tipo `FeedPost` condiviso non espone ancora alcuni
// flag che il backend serializza (is_bookmarked) né le reazioni v2 con
// reaction_kind. Definisco interface narrow locali per evitare `any`.
interface FeedPostExt extends FeedPost {
  is_bookmarked?: boolean;
}

interface FeedReactionExt extends FeedReaction {
  reaction_kind?: FeedReactionKind;
}

// Conteggi RSVP evento: DTO condiviso (`GET|POST /messenger/feed/posts/:id/rsvp`).
type RsvpCounts = FeedRsvpSummary;

// =============================================================================
// Task #1720 — Pagina mobile dedicata al Feed gruppi.
//
// Riusa integralmente le rotte REST/Socket del Feed (stesse di FeedPageDesktop)
// e le primitive mobile di #1718 (MobileTopBar + MobileComposerSheet). I
// sub-component (PostCard mobile, sheet filtri, sheet commenti, composer
// destinatari, picker poll) sono inline qui per rispettare il vincolo del
// task: UI feed mobile esclusivamente in `pages/mobile/` o
// `components/mobile/messenger/`. Nessuna duplicazione di componenti
// `feed/PostCard` desktop: layout completamente ridisegnato per touch.
// =============================================================================

type FilterKey = 'all' | 'following' | 'announcements' | 'mine' | 'bookmarks' | 'reports';
type OrderKey = 'for_you' | 'chrono';

interface ApiResp<T> {
  success: boolean;
  data?: T;
  nextCursor?: string | null;
}

interface RecipientPick {
  id: string;
  type: 'user' | 'workgroup' | 'department';
  name: string;
}

// DTO condivisi (`@easyflow/shared`) delle rotte Feed lette via apiClient.
type TopicMeta = Pick<FeedTopicListItem, 'id' | 'slug' | 'name' | 'color' | 'is_following'>;

interface PollDraft {
  question: string;
  options: string[];
  is_multiple: boolean;
  is_anonymous: boolean;
  closes_at: string;
}

interface FeedSocketCommentEvent {
  postId: string;
  comment: FeedComment;
}

interface FeedSocketReactionEvent {
  postId: string;
  emoji: string;
  reaction_kind?: FeedReactionKind;
}

const FILTER_TABS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'Tutti' },
  { key: 'following', label: 'Seguiti' },
  { key: 'announcements', label: 'Annunci' },
  { key: 'mine', label: 'I miei post' },
  { key: 'bookmarks', label: 'Salvati' },
  { key: 'reports', label: 'Segnalazioni' },
];

const REPORT_REASONS: Array<{
  value: 'spam' | 'harassment' | 'inappropriate' | 'off_topic' | 'misinformation' | 'other';
  label: string;
}> = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Molestie' },
  { value: 'inappropriate', label: 'Inappropriato' },
  { value: 'off_topic', label: 'Fuori tema' },
  { value: 'misinformation', label: 'Disinformazione' },
  { value: 'other', label: 'Altro' },
];


const REACTION_KINDS: Array<{ kind: FeedReactionKind; emoji: string; label: string }> = [
  { kind: 'like', emoji: '👍', label: 'Like' },
  { kind: 'love', emoji: '❤️', label: 'Love' },
  { kind: 'celebrate', emoji: '🎉', label: 'Celebra' },
  { kind: 'idea', emoji: '💡', label: 'Idea' },
  { kind: 'curious', emoji: '🤔', label: 'Curioso' },
  { kind: 'thanks', emoji: '🙏', label: 'Grazie' },
  { kind: 'applause', emoji: '👏', label: 'Applauso' },
];

const POST_TYPE_BADGE: Record<string, { label: string; icon: typeof Megaphone; color: string }> = {
  announcement: { label: 'Annuncio', icon: Megaphone, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  event: { label: 'Evento', icon: CalendarIcon, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  poll: { label: 'Sondaggio', icon: BarChart3, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  question: { label: 'Domanda', icon: HelpCircle, color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
  milestone: { label: 'Milestone', icon: Trophy, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  appreciation: { label: 'Apprezzamento', icon: Award, color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300' },
  file: { label: 'File', icon: Paperclip, color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
};

const AVATAR_GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-purple-500 to-violet-600',
  'from-orange-500 to-red-600',
  'from-pink-500 to-rose-600',
  'from-cyan-500 to-blue-600',
];

function gradientFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]!;
}

function isImageMime(mime?: string): boolean {
  return !!mime && mime.startsWith('image/');
}

function buildPollPayload(draft: PollDraft): {
  question: string;
  options: string[];
  is_multiple_choice: boolean;
  is_anonymous: boolean;
  closes_at?: string;
} | null {
  const q = draft.question.trim();
  const opts = draft.options.map((o) => o.trim()).filter((o) => o.length > 0);
  if (!q || opts.length < 2) return null;
  const payload: ReturnType<typeof buildPollPayload> = {
    question: q,
    options: opts,
    is_multiple_choice: draft.is_multiple,
    is_anonymous: draft.is_anonymous,
  };
  if (draft.closes_at) {
    payload!.closes_at = new Date(draft.closes_at).toISOString();
  }
  return payload;
}

const EMPTY_POLL: PollDraft = {
  question: '',
  options: ['', ''],
  is_multiple: false,
  is_anonymous: false,
  closes_at: '',
};

export default function MobileFeed(): JSX.Element {
  const { user } = useAuth();
  const { on, off } = useSocket();
  const { toast } = useToast();

  // ----- Data state -----
  const [posts, setPosts] = useState<FeedPostExt[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [newPostsBanner, setNewPostsBanner] = useState(0);
  const [rsvpMap, setRsvpMap] = useState<Record<string, RsvpCounts>>({});
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});

  // ----- Filters -----
  const [filter, setFilter] = useState<FilterKey>('all');
  const [order, setOrder] = useState<OrderKey>('for_you');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [topicId, setTopicId] = useState<string>('');
  const [topics, setTopics] = useState<TopicMeta[]>([]);

  // ----- Author/moderator flag -----
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [isEditor, setIsEditor] = useState(false);
  const isModerator = isAdmin || isEditor;

  // ----- UI state -----
  const [composerOpen, setComposerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [commentsFor, setCommentsFor] = useState<FeedPost | null>(null);
  const [commentsMap, setCommentsMap] = useState<Record<string, FeedComment[]>>({});
  const [editingPost, setEditingPost] = useState<FeedPost | null>(null);
  const [reportingPost, setReportingPost] = useState<{ id: string; title: string | null } | null>(null);
  const [reportReason, setReportReason] = useState<typeof REPORT_REASONS[number]['value']>('spam');
  const [reportDetail, setReportDetail] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const scrollerRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // Debounce search
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), 400);
    return () => window.clearTimeout(id);
  }, [search]);

  // ---------------------------------------------------------------------------
  // Bootstrap (topics + editor flag + counts)
  // ---------------------------------------------------------------------------
  const userId = user?.id;

  const loadTopics = useCallback(async () => {
    if (!userId) return;
    try {
      const d = await apiClient.get<FeedTopicListItem[]>('/messenger/feed/topics');
      if (d.success && Array.isArray(d.data)) setTopics(d.data);
    } catch {
      /* silent */
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    void loadTopics();
    if (!isAdmin) {
      apiClient.get<FeedEditorListItem[]>('/messenger/feed/editors')
        .then((d) => {
          if (d.success && Array.isArray(d.data)) {
            setIsEditor(d.data.some((e) => e.user_id === userId));
          }
        })
        .catch(() => { /* silent */ });
    } else {
      setIsEditor(true);
    }
  }, [loadTopics, isAdmin, userId]);

  useEffect(() => {
    apiClient
      .get<Record<string, number>>('/messenger/feed/posts/counts')
      .then((d) => {
        if (d?.success && d.data) setTabCounts(d.data);
      })
      .catch(() => { /* silent */ });
  }, [posts.length]);

  // ---------------------------------------------------------------------------
  // Fetch posts
  // ---------------------------------------------------------------------------
  const fetchPosts = useCallback(
    async (cursor?: string) => {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('filter', filter);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (cursor) params.set('cursor', cursor);
      params.set('order', order);
      if (topicId) params.set('topic_id', topicId);
      params.set('limit', '20');
      try {
        // La lista post risponde con `nextCursor` accanto all'envelope: payload top-level.
        const data = await apiClient.getTopLevel<ApiResp<FeedPostExt[]>>(`/messenger/feed/posts?${params.toString()}`);
        if (data.success) {
          const list = data.data || [];
          if (cursor) setPosts((prev) => [...prev, ...list]);
          else setPosts(list);
          setNextCursor(data.nextCursor || null);
          list
            .filter((p) => p.post_type === 'event')
            .forEach((p) => {
              apiClient
                .get<FeedRsvpSummary>(`/messenger/feed/posts/${p.id}/rsvp`)
                .then((r) => {
                  if (r?.success && r.data) {
                    const counts = r.data;
                    setRsvpMap((prev) => ({ ...prev, [p.id]: counts }));
                  }
                })
                .catch(() => { /* silent */ });
            });
        }
      } catch {
        toast({
          title: 'Errore',
          description: 'Impossibile caricare il feed',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filter, debouncedSearch, order, topicId, toast],
  );

  useEffect(() => {
    setLoading(true);
    void fetchPosts();
  }, [fetchPosts]);

  // ---------------------------------------------------------------------------
  // Socket events
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleNewPost = (data: FeedPost) => {
      if (scrollerRef.current && scrollerRef.current.scrollTop > 200) {
        setNewPostsBanner((p) => p + 1);
      } else {
        setPosts((prev) => [data, ...prev]);
      }
    };
    const handleNewComment = (data: FeedSocketCommentEvent) => {
      if (!data?.postId || !data.comment) return;
      setCommentsMap((prev) => ({
        ...prev,
        [data.postId]: [...(prev[data.postId] || []), data.comment],
      }));
      setPosts((prev) =>
        prev.map((p) =>
          p.id === data.postId
            ? { ...p, comment_count: (p.comment_count || 0) + 1 }
            : p,
        ),
      );
    };
    const handleReaction = (data: FeedSocketReactionEvent) => {
      if (!data?.postId || !data.emoji) return;
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id !== data.postId) return p;
          const reactions: FeedReactionExt[] = [...((p.reactions as FeedReactionExt[]) || [])];
          const existing = reactions.find(
            (r) =>
              r.emoji === data.emoji ||
              (data.reaction_kind ? r.reaction_kind === data.reaction_kind : false),
          );
          if (existing) {
            existing.count = (existing.count || 0) + 1;
          } else {
            const next: FeedReactionExt = { emoji: data.emoji, count: 1 };
            if (data.reaction_kind) next.reaction_kind = data.reaction_kind;
            reactions.push(next);
          }
          return { ...p, reactions };
        }),
      );
    };
    on('feed:newPost', handleNewPost);
    on('feed:newComment', handleNewComment);
    on('feed:reaction', handleReaction);
    return () => {
      off('feed:newPost', handleNewPost);
      off('feed:newComment', handleNewComment);
      off('feed:reaction', handleReaction);
    };
  }, [on, off]);

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const handleReact = useCallback(
    async (postId: string, kind: FeedReactionKind, emoji: string) => {
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id !== postId) return p;
          const reactions: FeedReactionExt[] = [...((p.reactions as FeedReactionExt[]) || [])];
          const existing = reactions.find(
            (r) => r.reaction_kind === kind || r.emoji === emoji,
          );
          if (existing) existing.count = (existing.count || 0) + 1;
          else reactions.push({ emoji, reaction_kind: kind, count: 1 });
          return { ...p, reactions };
        }),
      );
      try {
        await apiClient.post(`/messenger/feed/posts/${postId}/reactions-v2`, { kind, emoji });
      } catch {
        await apiClient
          .post(`/messenger/feed/posts/${postId}/reactions`, { emoji })
          .catch(() => { /* silent */ });
      }
    },
    [],
  );

  const handleBookmark = useCallback(
    async (postId: string, next: boolean) => {
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, is_bookmarked: next } : p)));
      try {
        if (next) await apiClient.post(`/messenger/feed/posts/${postId}/bookmark`, {});
        else await apiClient.delete(`/messenger/feed/posts/${postId}/bookmark`);
      } catch {
        setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, is_bookmarked: !next } : p)));
        toast({ title: 'Errore', description: 'Segnalibro non aggiornato', variant: 'destructive' });
      }
    },
    [toast],
  );

  const handleBoost = useCallback(
    async (postId: string, hours: number) => {
      try {
        const r = await apiClient.post<{ boost_until: string | null }>(
          `/messenger/feed/posts/${postId}/boost`,
          { hours },
        );
        if (r?.success && r.data) {
          const boostUntil = r.data.boost_until;
          setPosts((prev) =>
            prev.map((p) =>
              p.id === postId ? { ...p, boost_until: boostUntil } : p,
            ),
          );
          toast({ title: 'Boost attivato', description: `In evidenza per ${hours}h` });
        }
      } catch {
        toast({ title: 'Errore', description: 'Boost non riuscito', variant: 'destructive' });
      }
    },
    [toast],
  );

  const handleClearBoost = useCallback(async (postId: string) => {
    try {
      await apiClient.delete(`/messenger/feed/posts/${postId}/boost`);
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, boost_until: null } : p)));
    } catch {
      /* silent */
    }
  }, []);

  const handleDelete = useCallback(
    async (postId: string) => {
      if (!window.confirm('Eliminare questo post?')) return;
      try {
        await apiClient.delete(`/messenger/feed/posts/${postId}`);
        setPosts((prev) => prev.filter((p) => p.id !== postId));
      } catch {
        toast({ title: 'Errore', description: 'Eliminazione fallita', variant: 'destructive' });
      }
    },
    [toast],
  );

  const handleReportSubmit = useCallback(async () => {
    if (!reportingPost) return;
    setReportSubmitting(true);
    try {
      await apiClient.post(`/messenger/feed/posts/${reportingPost.id}/report`, {
        reason: reportReason,
        detail: reportDetail.trim() || undefined,
      });
      toast({ title: 'Segnalazione inviata', description: 'Grazie per il feedback.' });
      setReportingPost(null);
      setReportReason('spam');
      setReportDetail('');
    } catch {
      toast({ title: 'Errore', description: 'Segnalazione non inviata', variant: 'destructive' });
    } finally {
      setReportSubmitting(false);
    }
  }, [reportingPost, reportReason, reportDetail, toast]);

  const handleVotePoll = useCallback(
    async (pollId: string, optionId: string) => {
      try {
        await apiClient.post(`/messenger/feed/polls/${pollId}/vote`, { option_id: optionId });
        void fetchPosts();
      } catch {
        /* silent */
      }
    },
    [fetchPosts],
  );

  const handleRsvp = useCallback(
    async (postId: string, status: FeedRsvpStatus) => {
      try {
        const r = await apiClient.post<FeedRsvpSummary>(
          `/messenger/feed/posts/${postId}/rsvp`,
          { status },
        );
        if (r?.success && r.data) {
          const counts = r.data;
          setRsvpMap((prev) => ({ ...prev, [postId]: counts }));
        }
      } catch {
        toast({ title: 'Errore', description: 'RSVP non aggiornato', variant: 'destructive' });
      }
    },
    [toast],
  );

  const handleLoadComments = useCallback(async (postId: string) => {
    try {
      const data = await apiClient.get<FeedComment[]>(
        `/messenger/feed/posts/${postId}/comments`,
      );
      if (data?.success) {
        const list = (data.data || []) as FeedComment[];
        setCommentsMap((prev) => ({ ...prev, [postId]: list }));
      }
    } catch {
      /* silent */
    }
  }, []);

  const handleAddComment = useCallback(
    async (postId: string, text: string) => {
      const html = text
        .split(/\n+/)
        .map((p) => `<p>${escapeHtml(p)}</p>`)
        .join('');
      try {
        const data = await apiClient.post<FeedComment>(
          `/messenger/feed/posts/${postId}/comments`,
          { content_html: html, content_json: null as ContentJson },
        );
        if (data?.success && data.data) {
          const comment = data.data as FeedComment;
          setCommentsMap((prev) => ({
            ...prev,
            [postId]: [...(prev[postId] || []), comment],
          }));
          setPosts((prev) =>
            prev.map((p) =>
              p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p,
            ),
          );
        }
      } catch {
        toast({ title: 'Errore', description: 'Commento non inviato', variant: 'destructive' });
      }
    },
    [toast],
  );

  // ---------------------------------------------------------------------------
  // Pull-to-refresh
  // ---------------------------------------------------------------------------
  const handleRefresh = useCallback(async () => {
    setNewPostsBanner(0);
    await fetchPosts();
  }, [fetchPosts]);

  const pullState = usePullToRefresh(scrollerRef, { onRefresh: handleRefresh });
  const pullTranslate = pullState.refreshing ? 48 : pullState.pullDistance;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const activeFilterCount =
    (filter !== 'all' ? 1 : 0) + (order !== 'for_you' ? 1 : 0) + (topicId ? 1 : 0);

  const topbarActions: MobileTopBarAction[] = [
    {
      id: 'filters',
      label: 'Filtri',
      icon: <Filter className="w-5 h-5" />,
      onClick: () => setFiltersOpen(true),
      badge: activeFilterCount,
    },
    {
      id: 'compose',
      label: 'Nuovo post',
      icon: <PenSquare className="w-5 h-5" />,
      onClick: () => setComposerOpen(true),
    },
  ];

  return (
    <div className="flex flex-col h-full -m-3 sm:-m-4 bg-background" data-testid="mobile-feed-root">
      <MobileTopBar
        title="Feed"
        subtitle={
          filter !== 'all' || topicId || order !== 'for_you'
            ? `${FILTER_TABS.find((t) => t.key === filter)?.label || 'Tutti'}${
                topicId
                  ? ` · ${topics.find((t) => t.id === topicId)?.name || 'Topic'}`
                  : ''
              }`
            : 'Stream aziendale'
        }
        actions={topbarActions}
      />

      {/* Pull-to-refresh indicator */}
      {(pullState.pulling || pullState.refreshing) && (
        <div
          className="flex items-center justify-center text-xs text-muted-foreground"
          style={{ height: `${pullTranslate}px` }}
          data-testid="mobile-feed-pull"
        >
          {pullState.refreshing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw
              className="w-4 h-4 transition-transform"
              style={{
                transform: `rotate(${Math.min(pullState.pullDistance * 3, 360)}deg)`,
              }}
            />
          )}
        </div>
      )}

      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto overscroll-y-contain"
        data-testid="mobile-feed-scroller"
      >
        {newPostsBanner > 0 && (
          <button
            type="button"
            onClick={() => {
              setNewPostsBanner(0);
              if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
              void fetchPosts();
            }}
            className="sticky top-1 z-10 mx-auto mt-2 px-4 py-1.5 rounded-full bg-indigo-600 text-white text-xs font-semibold shadow-md flex items-center gap-1.5"
            style={{ background: auroraPalette.indigo }}
            data-testid="mobile-feed-newposts-banner"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {newPostsBanner} {newPostsBanner === 1 ? 'nuovo post' : 'nuovi post'}
          </button>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
            <p className={cn(aurora.type.caption, 'text-muted-foreground')}>
              Caricamento feed…
            </p>
          </div>
        ) : posts.length === 0 ? (
          <MobileFeedEmpty filter={filter} hasSearch={!!debouncedSearch} />
        ) : (
          <div className="flex flex-col gap-2 px-2 py-3" data-testid="mobile-feed-list">
            {posts.map((post, idx) => (
              <MobilePostCard
                key={post.id}
                post={post}
                index={idx}
                currentUserId={user?.id || ''}
                isModerator={isModerator}
                rsvp={rsvpMap[post.id] ?? null}
                onReact={handleReact}
                onBookmark={handleBookmark}
                onBoost={handleBoost}
                onClearBoost={handleClearBoost}
                onOpenComments={() => {
                  setCommentsFor(post);
                  if (!commentsMap[post.id]) void handleLoadComments(post.id);
                }}
                onEdit={() => setEditingPost(post)}
                onDelete={() => handleDelete(post.id)}
                onReport={() => setReportingPost({ id: post.id, title: post.title ?? null })}
                onVotePoll={handleVotePoll}
                onRsvp={handleRsvp}
              />
            ))}

            {nextCursor && (
              <div className="flex justify-center py-3">
                <Button
                  variant="outline"
                  className="rounded-full"
                  disabled={loadingMore}
                  onClick={() => {
                    if (!nextCursor || loadingMore) return;
                    setLoadingMore(true);
                    void fetchPosts(nextCursor);
                  }}
                  data-testid="mobile-feed-load-more"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carica altri
                    </>
                  ) : (
                    'Carica altri post'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters sheet */}
      <MobileFiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filter={filter}
        setFilter={setFilter}
        order={order}
        setOrder={setOrder}
        topicId={topicId}
        setTopicId={setTopicId}
        topics={topics}
        tabCounts={tabCounts}
        search={search}
        setSearch={setSearch}
      />

      {/* Composer sheet — nuovo post */}
      {composerOpen && (
        <MobileFeedComposer
          mode="create"
          open={composerOpen}
          onOpenChange={setComposerOpen}
          topics={topics}
          onPosted={(p) => {
            setPosts((prev) => [p, ...prev]);
            setComposerOpen(false);
            toast({ title: 'Pubblicato', description: 'Post pubblicato con successo' });
          }}
        />
      )}

      {/* Composer sheet — modifica post */}
      {editingPost && (
        <MobileFeedComposer
          mode="edit"
          open={!!editingPost}
          onOpenChange={(o) => {
            if (!o) setEditingPost(null);
          }}
          topics={topics}
          editingPost={editingPost}
          onPosted={(updated) => {
            setPosts((prev) =>
              prev.map((p) => (p.id === updated.id ? { ...p, ...updated, is_edited: true } : p)),
            );
            setEditingPost(null);
            toast({ title: 'Modificato', description: 'Post aggiornato' });
          }}
        />
      )}

      {/* Comments sheet (fullscreen) */}
      <MobileCommentsSheet
        post={commentsFor}
        comments={commentsFor ? commentsMap[commentsFor.id] : undefined}
        onOpenChange={(o) => {
          if (!o) setCommentsFor(null);
        }}
        onAdd={handleAddComment}
      />

      {/* Report dialog */}
      {reportingPost && (
        <Sheet
          open={!!reportingPost}
          onOpenChange={(o) => {
            if (!o && !reportSubmitting) {
              setReportingPost(null);
              setReportReason('spam');
              setReportDetail('');
            }
          }}
        >
          <SheetContent
            side="bottom"
            className="rounded-t-2xl max-h-[80dvh] overflow-y-auto pb-[max(env(safe-area-inset-bottom),16px)]"
          >
            <SheetHeader>
              <SheetTitle className="text-base flex items-center gap-2">
                <Flag className="w-4 h-4 text-orange-500" /> Segnala post
              </SheetTitle>
            </SheetHeader>
            <div className="mt-3 space-y-3">
              {reportingPost.title && (
                <p className="text-xs text-muted-foreground truncate">
                  Post: <strong>{reportingPost.title}</strong>
                </p>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 block">
                  Motivo
                </Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {REPORT_REASONS.map((r) => {
                    const active = reportReason === r.value;
                    return (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => setReportReason(r.value)}
                        className={cn(
                          'px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
                          active
                            ? 'bg-orange-600 text-white border-orange-600'
                            : 'bg-card text-foreground border-border hover:bg-muted',
                        )}
                        data-testid={`mobile-feed-report-reason-${r.value}`}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 block">
                  Dettagli (opzionale)
                </Label>
                <textarea
                  value={reportDetail}
                  onChange={(e) => setReportDetail(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  placeholder="Aggiungi contesto per i moderatori…"
                  data-testid="mobile-feed-report-detail"
                />
              </div>
              <Button
                type="button"
                onClick={() => void handleReportSubmit()}
                disabled={reportSubmitting}
                className="w-full rounded-full"
                style={{ background: '#ea580c' }}
                data-testid="mobile-feed-report-submit"
              >
                {reportSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Invio…
                  </>
                ) : (
                  'Invia segnalazione'
                )}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

// =============================================================================
// MobilePostCard
// =============================================================================

interface MobilePostCardProps {
  post: FeedPostExt;
  index: number;
  currentUserId: string;
  isModerator: boolean;
  rsvp: RsvpCounts | null;
  onReact: (postId: string, kind: FeedReactionKind, emoji: string) => void;
  onBookmark: (postId: string, next: boolean) => void;
  onBoost: (postId: string, hours: number) => void;
  onClearBoost: (postId: string) => void;
  onOpenComments: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onReport: () => void;
  onVotePoll: (pollId: string, optionId: string) => void;
  onRsvp: (postId: string, status: FeedRsvpStatus) => void;
}

function MobilePostCard({
  post,
  currentUserId,
  isModerator,
  rsvp,
  onReact,
  onBookmark,
  onBoost,
  onClearBoost,
  onOpenComments,
  onEdit,
  onDelete,
  onReport,
  onVotePoll,
  onRsvp,
}: MobilePostCardProps): JSX.Element {
  const [actionsOpen, setActionsOpen] = useState(false);
  const isAuthor = post.author_id === currentUserId;
  const authorName =
    `${post.author_first_name || ''} ${post.author_last_name || ''}`.trim() || 'Utente';
  const initials =
    `${(post.author_first_name || 'U')[0] || ''}${(post.author_last_name || '')[0] || ''}` || 'U';

  const typeMeta = POST_TYPE_BADGE[post.post_type];
  const reactions = post.reactions || [];
  const totalReactions = reactions.reduce((s, r) => s + (r.count || 0), 0);
  const isBoosted = !!(post.boost_until && new Date(post.boost_until) > new Date());

  const images = (post.attachments || []).filter((a) => isImageMime(a.mime_type));
  const files = (post.attachments || []).filter((a) => !isImageMime(a.mime_type));

  return (
    <article
      className={cn(
        'bg-card border border-border rounded-2xl overflow-hidden shadow-sm',
        post.is_pinned && 'ring-2 ring-amber-300/60',
      )}
      data-testid={`mobile-post-${post.id}`}
    >
      {(post.is_pinned || isBoosted) && (
        <div className="flex items-center gap-3 px-4 pt-3 text-[11px] font-semibold">
          {post.is_pinned && (
            <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <Pin className="w-3 h-3 rotate-45" /> Fissato
            </span>
          )}
          {isBoosted && (
            <span className="flex items-center gap-1 text-fuchsia-600 dark:text-fuchsia-400">
              <Rocket className="w-3 h-3" /> In evidenza
            </span>
          )}
        </div>
      )}

      <header className="flex items-start gap-3 px-4 pt-3">
        <Avatar className="h-10 w-10 ring-2 ring-background shadow-sm shrink-0">
          <AvatarImage src={post.author_avatar_url ?? undefined} />
          <AvatarFallback
            className={cn(
              'bg-gradient-to-br text-white font-semibold text-sm',
              gradientFor(authorName),
            )}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-sm text-foreground truncate">{authorName}</span>
            {typeMeta && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold',
                  typeMeta.color,
                )}
              >
                <typeMeta.icon className="w-3 h-3" /> {typeMeta.label}
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: it })}
            {post.is_edited && ' · modificato'}
            {post.target_type === 'global' && ' · Tutti'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setActionsOpen(true)}
          className="h-9 w-9 rounded-full inline-flex items-center justify-center text-muted-foreground hover:bg-muted active:bg-muted/80"
          aria-label="Altre azioni"
          data-testid={`mobile-post-actions-${post.id}`}
        >
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </header>

      <div className="px-4 pt-2 pb-3">
        {post.title && (
          <h3 className="text-base font-bold leading-snug text-foreground mb-1.5">
            {post.title}
          </h3>
        )}
        {post.content_html && (
          <div
            className="prose prose-sm dark:prose-invert max-w-none text-[14px] [&_a]:text-indigo-600 [&_img]:rounded-lg"
            dangerouslySetInnerHTML={{ __html: post.content_html }}
          />
        )}

        {images.length > 0 && (
          <div
            className={cn(
              'mt-3 grid gap-1.5 rounded-xl overflow-hidden',
              images.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
            )}
          >
            {images.slice(0, 4).map((att) => (
              <a
                key={att.id}
                href={att.url || att.file_url || `/api/messenger/feed/attachments/${att.id}/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-muted aspect-square overflow-hidden"
              >
                <img
                  src={att.url || att.file_url || `/api/messenger/feed/attachments/${att.id}/download`}
                  alt={att.filename}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </a>
            ))}
          </div>
        )}

        {files.length > 0 && (
          <div className="mt-3 flex flex-col gap-1.5">
            {files.map((att) => (
              <a
                key={att.id}
                href={att.url || att.file_url || `/api/messenger/feed/attachments/${att.id}/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-muted/60 rounded-lg border border-border text-sm hover:bg-muted active:bg-muted/80"
              >
                <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate font-medium">{att.filename}</span>
              </a>
            ))}
          </div>
        )}

        {post.post_type === 'poll' && post.poll && (
          <MobilePollCard
            poll={post.poll}
            onVote={(optionId) => onVotePoll(post.poll!.id, optionId)}
          />
        )}

        {post.post_type === 'event' && rsvp !== undefined && (
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            {(['yes', 'maybe', 'no'] as FeedRsvpStatus[]).map((s) => {
              const mine = rsvp?.my_status === s;
              const count = rsvp?.[s] ?? 0;
              const label = s === 'yes' ? 'Sì' : s === 'maybe' ? 'Forse' : 'No';
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => onRsvp(post.id, s)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                    mine
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-card text-foreground border-border hover:bg-muted',
                  )}
                  style={mine ? { background: auroraPalette.indigo } : undefined}
                >
                  {label}
                  {count > 0 && <span className="ml-1 opacity-80">· {count}</span>}
                </button>
              );
            })}
          </div>
        )}

        {post.topics && post.topics.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {post.topics.map((t: FeedTopic) => (
              <span
                key={t.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border"
                style={
                  t.color
                    ? {
                        backgroundColor: `${t.color}22`,
                        color: t.color,
                        borderColor: `${t.color}55`,
                      }
                    : undefined
                }
              >
                <Tag className="w-3 h-3" />
                {t.name}
              </span>
            ))}
          </div>
        )}

        {post.hashtags && post.hashtags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {post.hashtags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
              >
                <Hash className="w-3 h-3" />
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="border-t border-border grid grid-cols-4 px-1 py-1">
        <ActionBarButton
          icon={
            <span className="text-base leading-none">👍</span>
          }
          label={totalReactions > 0 ? String(totalReactions) : 'Reagisci'}
          onClick={() => onReact(post.id, 'like', '👍')}
          testId={`mobile-post-react-${post.id}`}
        />
        <ActionBarButton
          icon={<MessageCircle className="w-5 h-5" />}
          label={(post.comment_count ?? 0) > 0 ? String(post.comment_count) : 'Commenti'}
          onClick={onOpenComments}
          testId={`mobile-post-comments-${post.id}`}
        />
        <ActionBarButton
          icon={
            post.is_bookmarked ? (
              <BookmarkIcon className="w-5 h-5 fill-current text-amber-500" />
            ) : (
              <BookmarkPlus className="w-5 h-5" />
            )
          }
          label="Salva"
          active={!!post.is_bookmarked}
          onClick={() => onBookmark(post.id, !post.is_bookmarked)}
          testId={`mobile-post-bookmark-${post.id}`}
        />
        <ActionBarButton
          icon={<Rocket className={cn('w-5 h-5', isBoosted && 'fill-current text-fuchsia-500')} />}
          label="Boost"
          active={isBoosted}
          disabled={!isModerator || post.post_type !== 'announcement'}
          onClick={() => (isBoosted ? onClearBoost(post.id) : onBoost(post.id, 24))}
          testId={`mobile-post-boost-${post.id}`}
        />
      </div>

      {/* Reactions sheet */}
      <Sheet open={actionsOpen} onOpenChange={setActionsOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[max(env(safe-area-inset-bottom),16px)]">
          <SheetHeader>
            <SheetTitle className="text-base">Reagisci e azioni</SheetTitle>
          </SheetHeader>
          <div className="mt-3 grid grid-cols-7 gap-1">
            {REACTION_KINDS.map((r) => (
              <button
                key={r.kind}
                type="button"
                onClick={() => {
                  onReact(post.id, r.kind, r.emoji);
                  setActionsOpen(false);
                }}
                className="h-12 rounded-xl text-2xl hover:bg-muted active:bg-muted/80 flex items-center justify-center"
                aria-label={r.label}
              >
                {r.emoji}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-col">
            {isAuthor && (
              <SheetAction
                icon={<PenSquare className="w-5 h-5 text-indigo-600" />}
                label="Modifica post"
                onClick={() => {
                  setActionsOpen(false);
                  onEdit();
                }}
              />
            )}
            {!isAuthor && (
              <SheetAction
                icon={<Flag className="w-5 h-5 text-orange-500" />}
                label="Segnala post"
                onClick={() => {
                  setActionsOpen(false);
                  onReport();
                }}
              />
            )}
            {(isAuthor || isModerator) && (
              <SheetAction
                icon={<Trash2 className="w-5 h-5 text-red-500" />}
                label="Elimina post"
                destructive
                onClick={() => {
                  setActionsOpen(false);
                  onDelete();
                }}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </article>
  );
}

function ActionBarButton({
  icon,
  label,
  onClick,
  active,
  disabled,
  testId,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  testId?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg text-[11px] font-medium transition-colors',
        active ? 'text-foreground' : 'text-muted-foreground',
        disabled
          ? 'opacity-30 cursor-not-allowed'
          : 'hover:bg-muted active:bg-muted/80',
      )}
    >
      {icon}
      <span className="leading-none">{label}</span>
    </button>
  );
}

function SheetAction({
  icon,
  label,
  onClick,
  destructive,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-2 py-3 text-sm rounded-lg hover:bg-muted active:bg-muted/80',
        destructive ? 'text-red-600' : 'text-foreground',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

// =============================================================================
// MobilePollCard
// =============================================================================

function MobilePollCard({
  poll,
  onVote,
}: {
  poll: NonNullable<FeedPost['poll']>;
  onVote: (optionId: string) => void;
}): JSX.Element {
  const totalVotes = poll.total_votes || 0;
  const userVotes = poll.user_votes || [];
  return (
    <div className="mt-3 rounded-xl border border-border bg-muted/30 p-3">
      <p className="text-sm font-semibold text-foreground mb-2">{poll.question}</p>
      <div className="flex flex-col gap-1.5">
        {poll.options.map((opt) => {
          const pct = totalVotes > 0 ? Math.round(((opt.vote_count || 0) / totalVotes) * 100) : 0;
          const voted = userVotes.includes(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onVote(opt.id)}
              className={cn(
                'relative text-left px-3 py-2 rounded-lg border text-sm overflow-hidden transition-colors',
                voted ? 'border-indigo-400 bg-indigo-50/60 dark:bg-indigo-900/20' : 'border-border bg-card hover:bg-muted',
              )}
            >
              <div
                className="absolute inset-y-0 left-0 bg-indigo-200/40 dark:bg-indigo-700/30"
                style={{ width: `${pct}%` }}
                aria-hidden
              />
              <div className="relative flex items-center justify-between gap-2">
                <span className="font-medium flex items-center gap-1.5">
                  {voted && <CheckCircle2 className="w-4 h-4 text-indigo-600" />}
                  {opt.option_text}
                </span>
                <span className="text-xs text-muted-foreground">
                  {opt.vote_count || 0} · {pct}%
                </span>
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        {totalVotes} {totalVotes === 1 ? 'voto' : 'voti'}
        {poll.is_anonymous && ' · anonimo'}
        {poll.is_multiple_choice && ' · scelta multipla'}
      </p>
    </div>
  );
}

// =============================================================================
// MobileFeedEmpty
// =============================================================================

function MobileFeedEmpty({ filter, hasSearch }: { filter: FilterKey; hasSearch: boolean }): JSX.Element {
  let title = 'Nessun post nel feed';
  let subtitle = 'Inizia condividendo qualcosa con il tuo team.';
  if (filter === 'bookmarks') {
    title = 'Nessun segnalibro';
    subtitle = 'Salva i post che vuoi rileggere: appariranno qui.';
  } else if (hasSearch) {
    title = 'Nessun risultato';
    subtitle = 'Prova a modificare i termini di ricerca o rimuovere i filtri.';
  }
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center gap-2">
      <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center">
        <Sparkles className="w-7 h-7 text-muted-foreground" />
      </div>
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-xs">{subtitle}</p>
    </div>
  );
}

// =============================================================================
// MobileFiltersSheet
// =============================================================================

interface FiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filter: FilterKey;
  setFilter: (f: FilterKey) => void;
  order: OrderKey;
  setOrder: (o: OrderKey) => void;
  topicId: string;
  setTopicId: (id: string) => void;
  topics: TopicMeta[];
  tabCounts: Record<string, number>;
  search: string;
  setSearch: (s: string) => void;
}

function MobileFiltersSheet({
  open,
  onOpenChange,
  filter,
  setFilter,
  order,
  setOrder,
  topicId,
  setTopicId,
  topics,
  tabCounts,
  search,
  setSearch,
}: FiltersSheetProps): JSX.Element {
  const [popular, setPopular] = useState<FeedPopularPost[]>([]);
  const [trending, setTrending] = useState<FeedTrendingHashtag[]>([]);
  useEffect(() => {
    if (!open) return;
    apiClient.get<FeedPopularPost[]>('/messenger/feed/popular')
      .then((d) => {
        if (d.success) setPopular(d.data || []);
      })
      .catch(() => undefined);
    apiClient.get<FeedTrendingHashtag[]>('/messenger/feed/hashtags/trending')
      .then((d) => {
        if (d.success) setTrending(d.data || []);
      })
      .catch(() => undefined);
  }, [open]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[88dvh] overflow-y-auto pb-[max(env(safe-area-inset-bottom),16px)]">
        <SheetHeader>
          <SheetTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filtri Feed
          </SheetTitle>
        </SheetHeader>

        <div className="mt-3 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 block">
              Ricerca
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca nel feed…"
                className="pl-9"
                data-testid="mobile-feed-filter-search"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 block">
              Scopo
            </Label>
            <div className="grid grid-cols-2 gap-1.5">
              {FILTER_TABS.map((t) => {
                const count = tabCounts[t.key] ?? 0;
                const active = filter === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setFilter(t.key)}
                    className={cn(
                      'px-3 py-2 rounded-lg text-sm font-medium border flex items-center justify-between gap-2 transition-colors',
                      active
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-card text-foreground border-border hover:bg-muted',
                    )}
                    style={active ? { background: auroraPalette.indigo } : undefined}
                  >
                    <span>{t.label}</span>
                    {count > 0 && (
                      <span
                        className={cn(
                          'min-w-[20px] text-center text-[10px] font-bold rounded-full px-1.5 py-0.5',
                          active ? 'bg-white/20' : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 block">
              Ordinamento
            </Label>
            <div className="grid grid-cols-2 gap-1.5">
              {(
                [
                  { k: 'for_you' as const, label: 'Per te' },
                  { k: 'chrono' as const, label: 'Recenti' },
                ] satisfies Array<{ k: OrderKey; label: string }>
              ).map((o) => {
                const active = order === o.k;
                return (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setOrder(o.k)}
                    className={cn(
                      'px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
                      active
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-card text-foreground border-border hover:bg-muted',
                    )}
                    style={active ? { background: auroraPalette.indigo } : undefined}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {topics.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 block">
                Topic
              </Label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setTopicId('')}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium border',
                    topicId === ''
                      ? 'bg-foreground text-background border-foreground'
                      : 'bg-card text-foreground border-border',
                  )}
                >
                  Tutti
                </button>
                {topics.map((t) => {
                  const active = topicId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTopicId(active ? '' : t.id)}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1',
                        active && 'text-white border-transparent',
                      )}
                      style={
                        active
                          ? { background: t.color || auroraPalette.indigo }
                          : t.color
                            ? {
                                color: t.color,
                                borderColor: `${t.color}55`,
                                backgroundColor: `${t.color}11`,
                              }
                            : undefined
                      }
                    >
                      <Tag className="w-3 h-3" />
                      {t.name}
                      {t.is_following && <Sparkles className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {popular.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5 text-orange-500" /> Post popolari
              </Label>
              <div className="space-y-1">
                {popular.slice(0, 5).map((p) => {
                  const author = `${p.author_first_name || ''} ${p.author_last_name || ''}`.trim() || 'Utente';
                  return (
                    <div
                      key={p.id}
                      className="px-2 py-1.5 rounded-lg bg-muted/40 border border-border"
                      data-testid={`mobile-feed-popular-${p.id}`}
                    >
                      <p className="text-xs font-medium truncate">{p.title || 'Post'}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                        {author} · {p.reaction_count || 0} reazioni · {p.comment_count || 0} commenti
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {trending.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-blue-500" /> Hashtag di tendenza
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {trending.map((t) => {
                  const active = search === `#${t.tag}`;
                  return (
                    <button
                      key={t.tag}
                      type="button"
                      onClick={() => setSearch(active ? '' : `#${t.tag}`)}
                      className={cn(
                        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800/50',
                      )}
                      data-testid={`mobile-feed-trending-${t.tag}`}
                    >
                      <Hash className="w-3 h-3" />
                      {t.tag}
                      {typeof t.post_count === 'number' && t.post_count > 0 && (
                        <span className="text-[10px] opacity-70">{t.post_count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="w-full rounded-full"
            style={{ background: auroraPalette.indigo }}
          >
            Applica
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// MobileFeedComposer (create + edit)
// =============================================================================

interface ComposerProps {
  mode: 'create' | 'edit';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topics: TopicMeta[];
  editingPost?: FeedPost;
  onPosted: (post: FeedPost) => void;
}

function MobileFeedComposer({
  mode,
  open,
  onOpenChange,
  topics,
  editingPost,
  onPosted,
}: ComposerProps): JSX.Element {
  const { toast } = useToast();
  const isEdit = mode === 'edit' && !!editingPost;

  const [title, setTitle] = useState<string>(isEdit ? editingPost!.title || '' : '');
  const [text, setText] = useState<string>(
    isEdit ? stripHtml(editingPost!.content_html || '') : '',
  );
  const [files, setFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<FeedAttachment[]>(
    isEdit ? editingPost!.attachments || [] : [],
  );
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Poll toggle (only in create)
  const [pollEnabled, setPollEnabled] = useState(false);
  const [poll, setPoll] = useState<PollDraft>(EMPTY_POLL);

  // Recipients
  const [recipients, setRecipients] = useState<RecipientPick[]>(
    isEdit
      ? (editingPost!.recipients || []).map((r) => ({
          id: r.recipient_id,
          type: r.recipient_type,
          name: r.display_name || r.recipient_id,
        }))
      : [],
  );
  const [recipientsDirty, setRecipientsDirty] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientResults, setRecipientResults] = useState<RecipientPick[]>([]);
  const recipientTimerRef = useRef<number | undefined>(undefined);

  // Topics
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>(
    isEdit ? (editingPost!.topics || []).map((t) => t.id) : [],
  );
  const [suggestedTopics, setSuggestedTopics] = useState<FeedTopicSuggestion[]>([]);
  const suggestTimerRef = useRef<number | undefined>(undefined);
  const suggestAbortRef = useRef<AbortController | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Recipients search
  useEffect(() => {
    if (recipientTimerRef.current) window.clearTimeout(recipientTimerRef.current);
    if (!recipientSearch.trim() || recipientSearch.length < 2) {
      setRecipientResults([]);
      return;
    }
    recipientTimerRef.current = window.setTimeout(async () => {
      try {
        const q = encodeURIComponent(recipientSearch);
        const [usersRes, groupsRes, deptsRes] = await Promise.allSettled([
          apiClient.get<DirectoryUserLookup[]>(`/users/search?search=${q}&limit=5`),
          apiClient.get<WorkgroupLookup[]>(`/workgroups?search=${q}&limit=5`),
          apiClient.get<DepartmentLookup[]>(`/departments?search=${q}&limit=5`),
        ]);

        const all: RecipientPick[] = [];
        if (usersRes.status === 'fulfilled' && usersRes.value.success) {
          for (const u of usersRes.value.data || []) {
            const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.id;
            all.push({ id: u.id, type: 'user', name });
          }
        }
        if (groupsRes.status === 'fulfilled' && groupsRes.value.success) {
          for (const g of groupsRes.value.data || []) {
            all.push({ id: g.id, type: 'workgroup', name: g.name || g.id });
          }
        }
        if (deptsRes.status === 'fulfilled' && deptsRes.value.success) {
          for (const d of deptsRes.value.data || []) {
            all.push({ id: d.id, type: 'department', name: d.name || d.id });
          }
        }

        const hasGroupOrDept = recipients.some(
          (r) => r.type === 'workgroup' || r.type === 'department',
        );
        const filtered = all.filter(
          (r) =>
            !recipients.some((s) => s.id === r.id && s.type === r.type) &&
            !(hasGroupOrDept && (r.type === 'workgroup' || r.type === 'department')),
        );
        setRecipientResults(filtered);
      } catch {
        /* silent */
      }
    }, 300);
    return () => {
      if (recipientTimerRef.current) window.clearTimeout(recipientTimerRef.current);
    };
  }, [recipientSearch, recipients]);

  // Topic suggestions
  useEffect(() => {
    if (suggestTimerRef.current) window.clearTimeout(suggestTimerRef.current);
    if (topics.length === 0) {
      setSuggestedTopics([]);
      return;
    }
    const composite = `${title} ${text}`.trim();
    if (composite.length < 20) {
      setSuggestedTopics([]);
      return;
    }
    suggestTimerRef.current = window.setTimeout(async () => {
      if (suggestAbortRef.current) suggestAbortRef.current.abort();
      const ctrl = new AbortController();
      suggestAbortRef.current = ctrl;
      try {
        const html = textToHtml(text);
        const data = await apiClient.post<FeedTopicSuggestion[]>(
          '/messenger/feed/topics/suggest',
          { title, content_html: html },
          { signal: ctrl.signal },
        );
        if (data.success && Array.isArray(data.data)) setSuggestedTopics(data.data);
      } catch {
        /* silent */
      }
    }, 600);
    return () => {
      if (suggestTimerRef.current) window.clearTimeout(suggestTimerRef.current);
    };
  }, [title, text, topics.length]);

  const addRecipient = (r: RecipientPick) => {
    const hasUser = recipients.some((x) => x.type === 'user');
    const hasGroupOrDept = recipients.some(
      (x) => x.type === 'workgroup' || x.type === 'department',
    );
    const isGroupOrDept = r.type === 'workgroup' || r.type === 'department';
    if ((hasUser && isGroupOrDept) || (hasGroupOrDept && r.type === 'user')) {
      setRecipients([r]);
    } else if (hasGroupOrDept && isGroupOrDept) {
      setRecipients([r]);
    } else {
      setRecipients((prev) => [...prev, r]);
    }
    setRecipientsDirty(true);
    setRecipientSearch('');
    setRecipientResults([]);
  };

  const removeRecipient = (id: string, type: RecipientPick['type']) => {
    setRecipients((prev) => prev.filter((r) => !(r.id === id && r.type === type)));
    setRecipientsDirty(true);
  };

  const toggleTopic = (id: string) => {
    setSelectedTopicIds((prev) => {
      if (prev.includes(id)) return prev.filter((t) => t !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const handlePickFiles = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submitDisabled = useMemo(() => {
    if (submitting) return true;
    if (pollEnabled && !buildPollPayload(poll)) return true;
    const hasBody = !!text.trim() || files.length > 0 || pollEnabled;
    return !hasBody && !isEdit;
  }, [submitting, pollEnabled, poll, text, files.length, isEdit]);

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setSubmitting(true);
    try {
      if (isEdit && editingPost) {
        const originalTopicIds = (editingPost.topics || []).map((t) => t.id);
        const topicsChanged =
          selectedTopicIds.length !== originalTopicIds.length ||
          selectedTopicIds.some((id) => !originalTopicIds.includes(id));

        const fd = new FormData();
        if (title) fd.append('title', title);
        const html = textToHtml(text);
        if (html) fd.append('content_html', html);
        if (topicsChanged) fd.append('topic_ids', JSON.stringify(selectedTopicIds));
        if (recipientsDirty) {
          fd.append(
            'recipients',
            JSON.stringify(
              recipients.map((r) => ({ recipient_type: r.type, recipient_id: r.id })),
            ),
          );
        }
        if (removedAttachmentIds.length > 0) {
          fd.append('remove_attachment_ids', JSON.stringify(removedAttachmentIds));
        }
        for (const f of files) fd.append('files', f);
        const data = await apiClient.patch<FeedPost>(
          `/messenger/feed/posts/${editingPost.id}`,
          fd,
        );
        if (data?.success && data.data) onPosted(data.data as FeedPost);
        else throw new Error('edit_failed');
      } else {
        const html = textToHtml(text);
        const targetType = recipients.length > 0 ? 'direct' : 'global';
        const recipientsPayload =
          targetType === 'direct'
            ? recipients.map((r) => ({ recipient_type: r.type, recipient_id: r.id }))
            : undefined;
        const pollPayload = pollEnabled ? buildPollPayload(poll) : null;
        const postType = pollPayload ? 'poll' : 'message';

        if (files.length > 0) {
          const fd = new FormData();
          for (const f of files) fd.append('files', f);
          fd.append('post_type', postType);
          if (title) fd.append('title', title);
          if (html) fd.append('content_html', html);
          fd.append('target_type', targetType);
          if (recipientsPayload) fd.append('recipients', JSON.stringify(recipientsPayload));
          if (pollPayload) fd.append('poll', JSON.stringify(pollPayload));
          if (selectedTopicIds.length > 0) fd.append('topic_ids', JSON.stringify(selectedTopicIds));
          const result = await apiClient.post<FeedPost>('/messenger/feed/posts/upload', fd);
          if (!result.success || !result.data) throw new Error('upload_failed');
          onPosted(result.data);
        } else {
          const payload: Record<string, unknown> = {
            post_type: postType,
            target_type: targetType,
          };
          if (title) payload.title = title;
          if (html) payload.content_html = html;
          if (recipientsPayload) payload.recipients = recipientsPayload;
          if (pollPayload) payload.poll = pollPayload;
          if (selectedTopicIds.length > 0) payload.topic_ids = selectedTopicIds;
          const result = await apiClient.post<FeedPost>(
            '/messenger/feed/posts',
            payload,
          );
          if (!result?.success || !result.data) throw new Error('create_failed');
          onPosted(result.data as FeedPost);
        }
      }
    } catch {
      toast({
        title: 'Errore',
        description: isEdit ? 'Modifica fallita' : 'Pubblicazione fallita',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const hasGroupOrDept = recipients.some(
    (r) => r.type === 'workgroup' || r.type === 'department',
  );

  const extraSlot = (
    <div className="flex flex-col gap-3" data-testid="mobile-feed-composer-extra">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Titolo (opzionale)"
        className="font-semibold"
      />

      {/* Recipients */}
      <div className="rounded-lg border border-border bg-card p-2 focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring">
        <div className="flex items-center gap-1.5 flex-wrap">
          {recipients.length === 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300">
              <Globe className="w-3 h-3" /> Visibile a tutti
            </span>
          )}
          {recipients.map((r) => {
            const Icon = r.type === 'user' ? UserCircle : r.type === 'workgroup' ? Users : Building2;
            return (
              <span
                key={`${r.type}-${r.id}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
              >
                <Icon className="w-3 h-3" />
                {r.name}
                <button
                  type="button"
                  onClick={() => removeRecipient(r.id, r.type)}
                  aria-label="Rimuovi destinatario"
                  className="ml-0.5 text-indigo-400 hover:text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
        <input
          value={recipientSearch}
          onChange={(e) => setRecipientSearch(e.target.value)}
          placeholder="Aggiungi utente, gruppo, reparto…"
          className="mt-1 w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground"
        />
        {recipientResults.length > 0 && (
          <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-border bg-popover">
            {recipientResults.map((r) => {
              const Icon = r.type === 'user' ? UserCircle : r.type === 'workgroup' ? Users : Building2;
              const disabled = hasGroupOrDept && (r.type === 'workgroup' || r.type === 'department');
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  type="button"
                  disabled={disabled}
                  onClick={() => addRecipient(r)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted disabled:opacity-40 text-left"
                >
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{r.name}</span>
                  <span className="text-[10px] text-muted-foreground uppercase">{r.type}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Topics */}
      {topics.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
            Topic ({selectedTopicIds.length}/3)
          </p>
          <div className="flex flex-wrap gap-1.5">
            {topics.map((t) => {
              const active = selectedTopicIds.includes(t.id);
              const disabled = !active && selectedTopicIds.length >= 3;
              return (
                <button
                  key={t.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleTopic(t.id)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-[11px] font-medium border inline-flex items-center gap-1',
                    active && 'text-white border-transparent',
                    disabled && 'opacity-40',
                  )}
                  style={
                    active
                      ? { background: t.color || auroraPalette.indigo }
                      : t.color
                        ? {
                            color: t.color,
                            borderColor: `${t.color}55`,
                            backgroundColor: `${t.color}11`,
                          }
                        : undefined
                  }
                >
                  <Tag className="w-3 h-3" />
                  {t.name}
                </button>
              );
            })}
          </div>
          {suggestedTopics.filter((s) => !selectedTopicIds.includes(s.id)).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 dark:text-indigo-400">
                <Sparkles className="w-3 h-3" /> Suggeriti:
              </span>
              {suggestedTopics
                .filter((s) => !selectedTopicIds.includes(s.id))
                .map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleTopic(s.id)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300"
                  >
                    <Plus className="w-3 h-3" />
                    {s.name}
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Poll (only create) */}
      {!isEdit && (
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4 text-purple-500" /> Aggiungi sondaggio
            </Label>
            <Switch checked={pollEnabled} onCheckedChange={setPollEnabled} />
          </div>
          {pollEnabled && (
            <div className="mt-3 space-y-2">
              <Input
                value={poll.question}
                onChange={(e) => setPoll((p) => ({ ...p, question: e.target.value }))}
                placeholder="Qual è la domanda?"
                className="font-medium"
              />
              <div className="space-y-1.5">
                {poll.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={opt}
                      onChange={(e) =>
                        setPoll((p) => {
                          const next = [...p.options];
                          next[i] = e.target.value;
                          return { ...p, options: next };
                        })
                      }
                      placeholder={`Opzione ${i + 1}`}
                    />
                    {poll.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() =>
                          setPoll((p) => ({
                            ...p,
                            options: p.options.filter((_, j) => j !== i),
                          }))
                        }
                        className="text-muted-foreground hover:text-red-500 p-1"
                        aria-label="Rimuovi opzione"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {poll.options.length < 20 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPoll((p) => ({ ...p, options: [...p.options, ''] }))
                    }
                    className="rounded-full border-dashed"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Opzione
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between text-xs">
                <Label className="flex items-center gap-2">
                  <Switch
                    checked={poll.is_multiple}
                    onCheckedChange={(v) => setPoll((p) => ({ ...p, is_multiple: v }))}
                  />
                  Scelta multipla
                </Label>
                <Label className="flex items-center gap-2">
                  <Switch
                    checked={poll.is_anonymous}
                    onCheckedChange={(v) => setPoll((p) => ({ ...p, is_anonymous: v }))}
                  />
                  Anonimo
                </Label>
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">Scadenza (opzionale)</Label>
                <Input
                  type="datetime-local"
                  value={poll.closes_at}
                  onChange={(e) => setPoll((p) => ({ ...p, closes_at: e.target.value }))}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const attachmentsSlot =
    files.length > 0 || existingAttachments.length > 0 ? (
      <div className="flex flex-col gap-1.5">
        {existingAttachments.map((att) => {
          const removed = removedAttachmentIds.includes(att.id);
          return (
            <div
              key={att.id}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/40 text-sm',
                removed && 'opacity-40 line-through',
              )}
            >
              <Paperclip className="w-4 h-4 text-muted-foreground" />
              <span className="flex-1 truncate">{att.filename}</span>
              <button
                type="button"
                onClick={() =>
                  setRemovedAttachmentIds((prev) =>
                    prev.includes(att.id)
                      ? prev.filter((x) => x !== att.id)
                      : [...prev, att.id],
                  )
                }
                aria-label={removed ? 'Ripristina allegato' : 'Rimuovi allegato'}
                className="text-muted-foreground hover:text-red-500"
              >
                {removed ? <RefreshCw className="w-4 h-4" /> : <X className="w-4 h-4" />}
              </button>
            </div>
          );
        })}
        {files.map((f, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/40 text-sm"
          >
            <Paperclip className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1 truncate">{f.name}</span>
            <span className="text-xs text-muted-foreground">{Math.round(f.size / 1024)} KB</span>
            <button
              type="button"
              onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
              aria-label="Rimuovi file"
              className="text-muted-foreground hover:text-red-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    ) : null;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handlePickFiles}
        className="hidden"
      />
      <MobileComposerSheet
        open={open}
        onOpenChange={onOpenChange}
        title={isEdit ? 'Modifica post' : 'Nuovo post'}
        placeholder="Cosa vuoi condividere con il team?"
        value={text}
        onValueChange={setText}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitLabel={isEdit ? 'Salva' : 'Pubblica'}
        submitDisabled={submitDisabled}
        onPickAttachment={() => fileInputRef.current?.click()}
        attachments={attachmentsSlot}
        extra={extraSlot}
      />
    </>
  );
}

// =============================================================================
// MobileCommentsSheet
// =============================================================================

function MobileCommentsSheet({
  post,
  comments,
  onOpenChange,
  onAdd,
}: {
  post: FeedPost | null;
  comments: FeedComment[] | undefined;
  onOpenChange: (open: boolean) => void;
  onAdd: (postId: string, text: string) => Promise<void>;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (post) setDraft('');
  }, [post?.id]);

  const handleSend = async () => {
    if (!post || !draft.trim() || sending) return;
    setSending(true);
    try {
      await onAdd(post.id, draft.trim());
      setDraft('');
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={!!post} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[100dvh] max-h-[100dvh] flex flex-col p-0 gap-0"
        data-testid="mobile-feed-comments-sheet"
      >
        <header className="flex items-center gap-2 px-3 py-2 border-b border-border min-h-[56px]">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            aria-label="Chiudi"
            onClick={() => onOpenChange(false)}
          >
            <X className="w-5 h-5" />
          </Button>
          <h2 className={cn(aurora.type.title, 'flex-1 text-foreground truncate')}>
            Commenti
          </h2>
        </header>
        <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-3">
          {!comments ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              Nessun commento ancora. Inizia tu la conversazione.
            </div>
          ) : (
            comments.map((c) => {
              const name =
                `${c.author_first_name || ''} ${c.author_last_name || ''}`.trim() || 'Utente';
              const initials =
                `${(c.author_first_name || 'U')[0] || ''}${(c.author_last_name || '')[0] || ''}` ||
                'U';
              return (
                <div key={c.id} className="flex gap-2.5 items-start">
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={c.author_avatar_url ?? undefined} />
                    <AvatarFallback
                      className={cn(
                        'bg-gradient-to-br text-white font-semibold text-xs',
                        gradientFor(name),
                      )}
                    >
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="rounded-2xl bg-muted/60 px-3 py-2">
                      <p className="text-xs font-semibold text-foreground">{name}</p>
                      <div
                        className="text-sm prose prose-sm dark:prose-invert max-w-none [&_p]:my-0"
                        dangerouslySetInnerHTML={{ __html: c.content_html }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5 ml-2">
                      {formatDistanceToNow(new Date(c.created_at), {
                        addSuffix: true,
                        locale: it,
                      })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div
          className="border-t border-border px-2 py-2 flex items-end gap-2"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' }}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Aggiungi un commento…"
            rows={1}
            className="flex-1 resize-none rounded-2xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 max-h-32"
            data-testid="mobile-feed-comment-input"
          />
          <Button
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            className="h-10 w-10 rounded-full p-0 shrink-0"
            style={{ background: auroraPalette.indigo }}
            aria-label="Invia commento"
            data-testid="mobile-feed-comment-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// helpers
// =============================================================================

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function textToHtml(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return '';
  return trimmed
    .split(/\n+/)
    .map((p) => `<p>${escapeHtml(p)}</p>`)
    .join('');
}

function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<\/p>\s*<p>/gi, '\n')
    .replace(/<br\s*\/?>(\s*)/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}
